import { useForm, usePage } from '@inertiajs/react'
import AuthLayout from '@/layouts/AuthLayout'
import type { SharedProps } from '@/types'
import type { FormEvent } from 'react'
import { useState } from 'react'

interface MfaChallengeProps {
  backup_available: boolean
}

export default function MfaChallenge() {
  const { backup_available, flash } = usePage<{ props: SharedProps & MfaChallengeProps }>()
    .props as unknown as SharedProps & MfaChallengeProps
  const [useBackup, setUseBackup] = useState(false)

  const { data, setData, post, processing, transform } = useForm({
    code: '',
    backup_code: '',
    remember_device: false,
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // Send only the factor the user is actually using, so a stale value in the
    // other field can't shadow it server-side (the server checks backup_code
    // first).
    transform((d) =>
      useBackup
        ? { backup_code: d.backup_code, remember_device: d.remember_device }
        : { code: d.code, remember_device: d.remember_device }
    )
    post('/login/mfa')
  }

  return (
    <AuthLayout title="Two-factor authentication">
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <h2 className="card-title text-2xl font-bold mb-1">Enter your code</h2>

          {flash?.alert && (
            <div role="alert" className="alert alert-soft alert-error text-sm mb-2">
              {flash.alert}
            </div>
          )}

          {!useBackup ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-base-content/60">
                Open your authenticator app and enter the 6-digit code.
              </p>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Authentication code</legend>
                <div className="flex justify-center">
                  {/* No w-full: daisyUI positions the digits with a letter-spacing tuned to
                      the otp's NATURAL width, so stretching the label desynchronises the
                      caret from the cell grid and clips each digit against its right
                      border. Centre the component rather than widening it. */}
                  <label className="otp otp-lg">
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <input
                    type="text"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    value={data.code}
                    onChange={(e) => setData('code', e.target.value.replace(/\D/g, ''))}
                    required
                    autoFocus
                  />
                  </label>
                </div>
              </fieldset>

              <label className="label cursor-pointer justify-start gap-2">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={data.remember_device}
                  onChange={(e) => setData('remember_device', e.target.checked)}
                />
                <span className="label-text">Trust this device for 30 days</span>
              </label>

              <button type="submit" className="btn btn-primary w-full" disabled={processing}>
                {processing && <span className="loading loading-spinner loading-sm" />}
                Verify
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-base-content/60">
                Enter one of the backup codes you saved when you turned on two-factor
                authentication. Each code works once.
              </p>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Backup code</legend>
                <input
                  type="text"
                  autoComplete="one-time-code"
                  className="input w-full font-mono"
                  placeholder="XXXX-XXXX"
                  value={data.backup_code}
                  onChange={(e) => setData('backup_code', e.target.value)}
                  required
                  autoFocus
                />
              </fieldset>

              <label className="label cursor-pointer justify-start gap-2">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={data.remember_device}
                  onChange={(e) => setData('remember_device', e.target.checked)}
                />
                <span className="label-text">Trust this device for 30 days</span>
              </label>

              <button type="submit" className="btn btn-primary w-full" disabled={processing}>
                {processing && <span className="loading loading-spinner loading-sm" />}
                Verify backup code
              </button>
            </form>
          )}

          {backup_available && (
            <button
              type="button"
              className="link link-hover text-sm text-center mt-4"
              onClick={() => setUseBackup((v) => !v)}
            >
              {useBackup ? 'Use your authenticator app instead' : "Can't access your app? Use a backup code"}
            </button>
          )}

          <p className="text-sm text-center mt-2">
            <a href="/login" className="link link-hover text-base-content/60">
              Back to log in
            </a>
          </p>
        </div>
      </div>
    </AuthLayout>
  )
}
